import type { PlatformAdapter } from './platformAdapter.interface';
import type { AuthPayload, Diagnostics } from '../platform.types';
import { setTgInitData } from '../../shared/auth/tokenStorage';

declare global {
  interface Window {
    Telegram?: any;
  }
}

export class TelegramAdapter implements PlatformAdapter {
  platform = 'telegram' as const;
  private initData: string | null = null;
  private webAppAvailable = false;

  canDetect(): boolean {
    return typeof window !== 'undefined' && !!window.Telegram?.WebApp;
  }

  private extractInitDataFromUrl(): string | null {
    if (typeof window === 'undefined') return null;
    const search = window.location.search.startsWith('?') ? window.location.search.slice(1) : window.location.search;
    const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
    const fromSearch = new URLSearchParams(search).get('tgWebAppData');
    const fromHash = new URLSearchParams(hash).get('tgWebAppData');
    return fromSearch || fromHash || null;
  }

  async init(): Promise<void> {
    try {
      const wa = window.Telegram?.WebApp;
      let urlInitData = this.extractInitDataFromUrl();
      if (wa) {
        try {
          wa.ready?.();
          wa.expand?.();
        } catch {
          /* ignore */
        }
        // В Telegram Web (desktop) initData часто приходит асинхронно от родителя — даём время появиться.
        let data = wa.initData || urlInitData || '';
        if (!data && typeof window !== 'undefined') {
          await new Promise((r) => setTimeout(r, 150));
          urlInitData = this.extractInitDataFromUrl();
          data = window.Telegram?.WebApp?.initData || urlInitData || '';
        }
        if (!data) {
          await new Promise((r) => setTimeout(r, 350));
          data = window.Telegram?.WebApp?.initData || this.extractInitDataFromUrl() || '';
        }
        this.webAppAvailable = true;
        this.initData = data || null;
        if (data) setTgInitData(data);
      } else if (urlInitData) {
        this.webAppAvailable = true;
        this.initData = urlInitData;
        setTgInitData(urlInitData);
      } else {
        this.webAppAvailable = false;
        this.initData = null;
      }
    } catch {
      this.webAppAvailable = false;
      this.initData = null;
    }
  }

  async getAuthPayload(): Promise<AuthPayload | null> {
    if (!this.webAppAvailable) return null;
    const data = this.initData || window.Telegram?.WebApp?.initData || this.extractInitDataFromUrl();
    if (!data || typeof data !== 'string' || data.length === 0) return null;
    return {
      kind: 'telegram',
      payload: { initData: data },
    };
  }

  getDiagnostics(): Diagnostics {
    const wa = typeof window !== 'undefined' ? window.Telegram?.WebApp : null;
    return {
      webAppAvailable: this.webAppAvailable,
      initDataLength: (this.initData || wa?.initData || '')?.length || 0,
      themeParams: wa?.themeParams ?? null,
      version: wa?.version ?? null,
      platform: wa?.platform ?? null,
      startParam: wa?.initDataUnsafe?.start_param ?? null,
    };
  }

  close(): void {
    try {
      window.Telegram?.WebApp?.close?.();
    } catch {
      // no-op
    }
  }
}
